import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrSuperAdmin, isUberAdmin, isDirectorOrAdmin, canManageClubs } from '../utils/auth';
import { getProfilePicture } from '../utils/profilePicture';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { user, actingAs, switchBackToParent } = useAuth();
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setAdminDropdownOpen(false);
      }
    };

    if (adminDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [adminDropdownOpen]);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isAdmin = isAdminOrSuperAdmin(user?.profileType);
  const isSuperAdmin = isUberAdmin(user?.profileType);
  const isDirector = user?.profileType === 'director';
  const canManageTeams = user?.profileType === 'manager' || user?.profileType === 'director' || isAdminOrSuperAdmin(user?.profileType);
  const canApproveRequests = user?.profileType === 'manager' || isAdminOrSuperAdmin(user?.profileType);

  const isParent = user?.profileType === 'parent';

  return (
    <>
    {actingAs && (
      <div style={{
        background: '#1565C0',
        color: 'white',
        padding: '8px 20px',
        textAlign: 'center',
        fontSize: '14px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '15px',
      }}>
        <span>Acting as <strong>{actingAs.firstName} {actingAs.lastName}</strong></span>
        <button
          onClick={switchBackToParent}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.5)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Switch Back
        </button>
      </div>
    )}
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/dashboard" className="navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src="/pp-logo.png" alt="Pitch Pilot" style={{ width: '25px', height: '25px', filter: 'invert(1)' }} />
          Pitch Pilot
        </Link>
        {user && (
          <>
          <button
            className="navbar-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
          <div className={`navbar-menu${menuOpen ? ' open' : ''}`}>
            <Link to="/teams" className="navbar-link" onClick={() => setMenuOpen(false)}>Teams</Link>
            <Link to="/players" className="navbar-link" onClick={() => setMenuOpen(false)}>Players</Link>
            <Link to="/leagues" className="navbar-link" onClick={() => setMenuOpen(false)}>Leagues</Link>
            <Link to="/tournaments" className="navbar-link" onClick={() => setMenuOpen(false)}>Tournaments</Link>
            <Link to="/matches" className="navbar-link" onClick={() => setMenuOpen(false)}>Matches</Link>
            <Link to="/stats" className="navbar-link" onClick={() => setMenuOpen(false)}>Stats</Link>
            {user.profileType === 'referee' && (
              <>
              <Link to="/referees/dashboard" className="navbar-link" onClick={() => setMenuOpen(false)}>Referee Dashboard</Link>
              <Link to="/match-day" className="navbar-link" onClick={() => setMenuOpen(false)}
                style={{ color: '#FFC107', fontWeight: '600' }}>
                Match Day
              </Link>
              </>
            )}
            {isParent && (
              <Link to="/parent-dashboard" className="navbar-link" onClick={() => setMenuOpen(false)}>Parent Dashboard</Link>
            )}
            
            {/* Other Dropdown */}
            {(isAdmin || canManageTeams || canApproveRequests || isDirector) && (
              <div className="navbar-dropdown" ref={dropdownRef}>
                <button
                  className="navbar-link navbar-dropdown-toggle"
                  onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                  style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '5px 10px', fontSize: 'inherit', fontFamily: 'inherit' }}
                >
                  Other
                </button>
                {adminDropdownOpen && (
                  <div className="navbar-dropdown-menu">
                    <Link to="/fields" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                      Fields & Facilities
                    </Link>
                    {isSuperAdmin && (
                      <Link to="/teams/approvals" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        Team Approvals
                      </Link>
                    )}
                    {canApproveRequests && (
                      <Link to="/team-join-approvals" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        Join Requests
                      </Link>
                    )}
                    {isSuperAdmin && (
                      <Link to="/admin/change-logs" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        Change Logs
                      </Link>
                    )}
                    {isSuperAdmin && (
                      <Link to="/admin/merge-accounts" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        Merge Accounts
                      </Link>
                    )}
                    {isAdmin && (
                      <Link to="/seasons" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        Seasons
                      </Link>
                    )}
                    {isAdmin && (
                      <Link to="/seasons/create" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        New Season
                      </Link>
                    )}
                    {isAdmin && (
                      <Link to="/scheduler" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        Scheduler
                      </Link>
                    )}
                    {isDirector && (
                      <Link to="/clubs/manage" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        My Clubs
                      </Link>
                    )}
                    {canManageTeams && (
                      <Link to="/clubs/create" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        New Club
                      </Link>
                    )}
                    {(isAdmin || isDirector) && (
                      <Link to="/teams/create" className="navbar-dropdown-item" onClick={() => { setAdminDropdownOpen(false); setMenuOpen(false); }}>
                        New Team
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            <Link to={`/users/${user.id}`} onClick={() => setMenuOpen(false)} style={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={getProfilePicture(user)}
                alt={`${user.firstName} ${user.lastName}`}
                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }}
              />
            </Link>
          </div>
          </>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navbar;

